import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import http from "../Utils/axiosConfig";
import downloadExcel, { billToRow, milkEntryToRow, fmtNum, fmtDate, toNum, rangeTag, EXPORT_LIMIT, buildStyledSheet } from "../Utils/excelExport";
import MilkEntryTable from "./MilkEntryTable";
import "./MemberRegister/Details/Details.css";
import "./MemberRegister/Main/Dashboard.css";

const PAGE_SIZE = 10;
const fmt = (v) => Number(v || 0).toFixed(2);

const BillListView = ({ mem_id }) => {
  // No date filter by default — the list behind the detail shows every bill, newest
  // first, so a member is never met with an empty screen. The filter is opt-in.
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [bills, setBills] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searched, setSearched] = useState(false);
  // Holds the loading placeholder until the newest-bill probe has resolved, so the
  // list doesn't flash on screen for a moment before the detail replaces it.
  const [booting, setBooting] = useState(true);

  // Range-level totals (all bills in filter, not just current page)
  const [rangeTotals, setRangeTotals] = useState({ milk: 0, rate: 0, payable: 0, count: 0 });

  const [selectedBill, setSelectedBill] = useState(null);
  const [detailRecords, setDetailRecords] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [dlOpen, setDlOpen] = useState(false);
  const [dlLoading, setDlLoading] = useState(false);

  const fetchBills = async (pageNum, f = from, t = to) => {
    setLoading(true);
    try {
      const res = await http.post("data/get_bill_list", {
        mem_id, from_date: f, to_date: t, page: pageNum, limit: PAGE_SIZE,
      });
      if (res.code === "DATA_FOUND") {
        setBills(res.data.records || []);
        setTotal(res.data.total || 0);
      } else {
        setBills([]); setTotal(0);
      }
    } catch (err) {
      console.error("Error fetching bill list:", err);
    } finally {
      setLoading(false); setSearched(true);
    }
  };

  // Fetch all bills for range totals
  const fetchRangeTotals = async (f, t) => {
    try {
      const res = await http.post("data/get_bill_list", {
        mem_id, from_date: f, to_date: t, page: 1, limit: EXPORT_LIMIT,
      });
      if (res.code === "DATA_FOUND") {
        const all = res.data.records || [];
        setRangeTotals({
          count: res.data.total || all.length,
          milk: all.reduce((s, b) => s + Number(b.totalmilk || 0), 0),
          rate: all.reduce((s, b) => s + Number(b.totalrate || 0), 0),
          payable: all.reduce((s, b) => s + Number(b.grandtotal || 0), 0),
        });
      } else {
        setRangeTotals({ milk: 0, rate: 0, payable: 0, count: 0 });
      }
    } catch (err) {
      console.error("Error fetching bill range totals:", err);
    }
  };

  // Owns every page-driven fetch, including the first load. The previous
  // `if (page !== 1)` guard meant paging back to page 1 never refetched, so the
  // table kept showing page 2's rows while the indicator read "1 / N".
  useEffect(() => {
    fetchBills(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Open the newest bill straight away — that is what the member came for. Asking
  // for one row with no date range always finds it, however long ago it was billed.
  useEffect(() => {
    fetchRangeTotals(from, to);
    (async () => {
      const res = await http.post("data/get_bill_list", { mem_id, page: 1, limit: 1 });
      const latest = res.code === "DATA_FOUND" ? (res.data.records || [])[0] : null;
      if (latest) openDetail(latest);
      setBooting(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    // Setting page to 1 only refires the effect when the value actually
    // changes, so fetch directly when already on page 1.
    if (page === 1) fetchBills(1, from, to);
    else setPage(1);
    fetchRangeTotals(from, to);
  };

  const openDetail = async (bill) => {
    setSelectedBill(bill);
    setDetailLoading(true);
    try {
      const res = await http.post("data/get_milk_entries", {
        mem_id, from_date: bill.from_date, to_date: bill.to_date, page: 1, limit: 100,
      });
      setDetailRecords(res.code === "DATA_FOUND" ? res.data.records : []);
    } catch (err) {
      console.error("Error fetching bill detail entries:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setSelectedBill(null); setDetailRecords([]); };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // ── Download helpers ────────────────────────────────────────────────────────
  const buildListSummaryAoa = (rows) => ([
    ["एकूण बिले", rows.length],
    ["एकूण दूध (लि.)", toNum(rows.reduce((s, b) => s + Number(b.totalmilk || 0), 0))],
    ["एकूण रक्कम (₹)", toNum(rows.reduce((s, b) => s + Number(b.totalrate || 0), 0))],
    ["एकूण देय रक्कम (₹)", toNum(rows.reduce((s, b) => s + Number(b.grandtotal || 0), 0))],
  ]);

  const doListDownload = (rows) => {
    if (!rows.length) return;
    downloadExcel({
      fileName: `bill-list-${rangeTag(from, to)}.xlsx`,
      sheetName: "बिल तपशील",
      rows: rows.map(billToRow),
      summaryAoa: buildListSummaryAoa(rows),
      from, to,
    });
  };

  const handleDownloadPage = () => { setDlOpen(false); doListDownload(bills); };

  const handleDownloadAll = async () => {
    setDlLoading(true);
    try {
      const res = await http.post("data/get_bill_list", {
        mem_id, from_date: from, to_date: to, page: 1, limit: EXPORT_LIMIT,
      });
      const all = res.code === "DATA_FOUND" ? res.data.records || [] : [];
      if (res.code === "DATA_FOUND" && (res.data.total || 0) > all.length) {
        alert(`या कालावधीत ${res.data.total} बिले आहेत; पहिली ${all.length} डाउनलोड होत आहेत. कृपया कालावधी लहान करा.`);
      }
      doListDownload(all);
    } catch (err) {
      console.error("Error downloading all bills:", err);
    } finally {
      setDlLoading(false); setDlOpen(false);
    }
  };

  const handleDownloadDetail = () => {
    if (!selectedBill || !detailRecords.length) return;
    const fromStr = selectedBill.from_date.split("T")[0];
    const toStr = selectedBill.to_date.split("T")[0];
    // Deductions as negative numbers rather than "- 123.00" strings, so the
    // column stays numeric and the member can total it themselves.
    const neg = (v) => (toNum(v) === "" ? "" : -toNum(v));
    const summaryAoa = [
      ["── बिल सारांश ──"],
      ["एकूण दूध (लि.)", toNum(selectedBill.totalmilk)],
      ["एकूण रक्कम (₹)", toNum(selectedBill.totalrate)],
      ["ऍडव्हान्स कपात (₹)", neg(selectedBill.advance_debit)],
      ["बँक भरणा (₹)", neg(selectedBill.bank)],
      ["पशुखाद्य कपात (₹)", neg(selectedBill.supply_debit)],
      ["देय रक्कम (₹)", toNum(selectedBill.grandtotal)],
    ];
    const ws = buildStyledSheet({
      rows: detailRecords.map(milkEntryToRow),
      summaryAoa,
      from: fromStr,
      to: toStr,
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "बिल");
    XLSX.writeFile(wb, `bill-${fromStr}-${toStr}.xlsx`);
  };

  // ── Detail view ─────────────────────────────────────────────────────────────
  if (selectedBill) {
    return (
      <div className="table-container">
        <div className="bill-detail-actions">
          <button className="dre-submit-btn" onClick={closeDetail}>← सर्व बिले</button>
          <button className="download-btn" onClick={handleDownloadDetail} disabled={detailLoading} title="Excel डाउनलोड करा">
            ⬇ डाउनलोड
          </button>
        </div>

        <div className="bill-period-header">
          <span>{fmtDate(selectedBill.from_date)}</span>
          <span> — </span>
          <span>{fmtDate(selectedBill.to_date)}</span>
        </div>

        {detailLoading ? (
          <div className="no-data"><p>लोड होत आहे...</p></div>
        ) : (
          <MilkEntryTable records={detailRecords} />
        )}

        <div className="bill-summary-readonly">
          <div className="summary-row"><span>एकूण दूध</span><span className="summary-value">{fmt(selectedBill.totalmilk)} लि.</span></div>
          <div className="summary-row"><span>एकूण रक्कम</span><span className="summary-value">₹{fmt(selectedBill.totalrate)}</span></div>
          <div className="summary-row"><span>ऍडव्हान्स कपात</span><span className="summary-value deduction">- ₹{fmt(selectedBill.advance_debit)}</span></div>
          <div className="summary-row"><span>बँक भरणा</span><span className="summary-value deduction">- ₹{fmt(selectedBill.bank)}</span></div>
          <div className="summary-row"><span>पशुखाद्य कपात</span><span className="summary-value deduction">- ₹{fmt(selectedBill.supply_debit)}</span></div>
          <div className="summary-row gross-row"><span>देय रक्कम</span><span className="summary-value gross">₹{fmt(selectedBill.grandtotal)}</span></div>
        </div>
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────────
  return (
    <div className="table-container">
      {/* Date filter is opt-in, so closed it costs one content-width pill */}
      <div className="filter-accordion">
        <button className="filter-accordion-head" onClick={() => setShowFilter((s) => !s)}
          aria-expanded={showFilter} title="कालावधी बदला">
          <span className="fa-label">📅 कालावधी</span>
          <span className="fa-value">{from && to ? `${fmtDate(from)} — ${fmtDate(to)}` : "सर्व बिले"}</span>
          <span className="fa-action">{showFilter ? "बंद करा ▲" : "बदला ▼"}</span>
        </button>
        <button className="download-btn" onClick={() => setDlOpen(true)}
          disabled={loading || bills.length === 0} title="Excel डाउनलोड करा">
          ⬇ डाउनलोड
        </button>
      </div>

      {showFilter && (
        <div className="pr-filter-bar">
          <div className="pr-filter-group">
            <label>पासून</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="pr-filter-group">
            <label>पर्यंत</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <button className="pr-search-btn" onClick={handleSearch} disabled={loading}>
            {loading ? "..." : "शोधा"}
          </button>
        </div>
      )}

      {/* Summary cards — full range */}
      {!loading && !booting && rangeTotals.count > 0 && (
        <div className="summary-cards">
          <div className="summary-card">
            <span className="summary-card-label">एकूण बिले</span>
            <span className="summary-card-value white">{rangeTotals.count}</span>
          </div>
          <div className="summary-card">
            <span className="summary-card-label">दूध (लि.)</span>
            <span className="summary-card-value">{fmtNum(rangeTotals.milk)}</span>
          </div>
          <div className="summary-card">
            <span className="summary-card-label">एकूण रक्कम</span>
            <span className="summary-card-value">₹{fmtNum(rangeTotals.rate)}</span>
          </div>
          <div className="summary-card">
            <span className="summary-card-label">देय रक्कम</span>
            <span className="summary-card-value green">₹{fmtNum(rangeTotals.payable)}</span>
          </div>
        </div>
      )}

      {loading || booting ? (
        <div className="no-data"><p>लोड होत आहे...</p></div>
      ) : searched && bills.length === 0 ? (
        <div className="no-data"><p>या कालावधीसाठी बिल उपलब्ध नाहीत.</p></div>
      ) : (
        <>
          <div className="table-wrapper">
            <table className="emc-table">
              <thead>
                <tr>
                  <th>पासून</th><th>पर्यंत</th><th>दूध (लि.)</th>
                  <th>एकूण दर</th><th>कपात</th><th>देय रक्कम</th><th></th>
                </tr>
              </thead>
              <tbody>
                {bills.map((b, i) => (
                  <tr key={i}>
                    <td>{fmtDate(b.from_date)}</td>
                    <td>{fmtDate(b.to_date)}</td>
                    <td>{fmt(b.totalmilk)}</td>
                    <td>₹{fmt(b.totalrate)}</td>
                    <td>₹{fmt(b.cutting)}</td>
                    <td>₹{fmt(b.grandtotal)}</td>
                    <td><button className="pr-detail-btn" onClick={() => openDetail(b)}>तपशील</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button className="page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>&#8249;</button>
              <span className="page-info">{page} / {totalPages}</span>
              <button className="page-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>&#8250;</button>
            </div>
          )}
        </>
      )}

      {dlOpen && (
        <div className="dl-modal-overlay" onClick={() => setDlOpen(false)}>
          <div className="dl-modal" onClick={(e) => e.stopPropagation()}>
            <h3>📥 Excel डाउनलोड करा</h3>
            <p>कोणत्या नोंदी डाउनलोड करायच्या आहेत?</p>
            <div className="dl-modal-actions">
              <button className="dl-modal-btn page" onClick={handleDownloadPage}>फक्त हे पान ({bills.length} बिले)</button>
              <button className="dl-modal-btn all" onClick={handleDownloadAll} disabled={dlLoading}>
                {dlLoading ? "लोड होत आहे..." : `सर्व नोंदी (${total} एकूण)`}
              </button>
              <button className="dl-modal-btn cancel" onClick={() => setDlOpen(false)}>रद्द करा</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillListView;
